import { useState, useMemo, useCallback } from 'react';
import { get } from 'lodash';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { buildTreeFromFlat, convertToTreeTableFormat, FlatOrgData, FlatOrgTreeTableNode } from 'src/utils/treeNode';
import { useScrollToNewItem } from './useScrollToNewItem';

interface UseOrgTableDataProps {
  data: FlatOrgData[];
  from?: string;
  searchTerm: string;
}

interface UseOrgTableDataReturn {
  filteredData: FlatOrgTreeTableNode[];
  expandedKeys: Record<string, boolean>;
  setExpandedKeys: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sortField: string;
  sortOrder: 0 | 1 | -1 | null | undefined;
  onSort: (sortField: string) => void;
  parentOrgNameById: Map<string, string | null>;
  /** Maps item id to its tree nesting level (1-based) */
  levelById: Map<string, number>;
}

/**
 * Custom hook for OrgTable data transformation, sorting, filtering, and state management.
 */
export const useOrgTableData = ({ data, from, searchTerm }: UseOrgTableDataProps): UseOrgTableDataReturn => {
  const [sortField, setSortField] = useLocalStorage<string>(`${TableIds.rootHerarchy}-sort-field`, '');
  const [sortOrder, setSortOrder] = useLocalStorage<0 | 1 | -1 | null | undefined>(
    `${TableIds.rootHerarchy}-sort-order`,
    1,
  );
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  // Build tree structure from flat data
  const treeData = useMemo(() => buildTreeFromFlat(data, from), [data, from]);
  const treeTableData = useMemo(() => convertToTreeTableFormat(treeData), [treeData]);

  useScrollToNewItem(data, treeTableData, setExpandedKeys);

  // Custom sort function that keeps workspaces before orgs at each level
  const customSortFunction = useCallback(
    (nodes: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      return [...nodes].sort((a, b) => {
        // Workspaces always come before organisations
        if (a.data.recordType !== b.data.recordType) {
          return a.data.recordType === 'W' ? -1 : 1;
        }

        if (sortField && sortOrder !== 0 && sortOrder !== null && sortOrder !== undefined) {
          const valueA = get(a.data, sortField);
          const valueB = get(b.data, sortField);

          if (valueA === undefined) return 1;
          if (valueB === undefined) return -1;

          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return sortOrder * valueA.localeCompare(valueB);
          }

          return sortOrder * (valueA - valueB);
        }

        return 0;
      });
    },
    [sortField, sortOrder],
  );

  // Recursively apply sort to tree structure
  const applyCustomSort = useCallback(
    (dataArray: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      const sortedData = customSortFunction(dataArray);

      return sortedData.map((node) => {
        if (node.children && node.children.length > 0) {
          return { ...node, children: applyCustomSort(node.children) };
        }
        return node;
      });
    },
    [customSortFunction],
  );

  // Filter tree by search term (case-insensitive, mid-word match on name)
  const filterData = useCallback((nodes: FlatOrgTreeTableNode[], term: string): FlatOrgTreeTableNode[] => {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) return JSON.parse(JSON.stringify(nodes));

    const normalizedTerm = trimmedTerm.toLowerCase();
    const matchesNode = (node: FlatOrgTreeTableNode) => node.data.name?.toLowerCase().includes(normalizedTerm);

    return nodes
      .filter((node) => {
        if (matchesNode(node)) return true;

        if (node.children && node.children.length > 0) {
          const filteredChildren = filterData(node.children, term);
          if (filteredChildren.length) {
            return true;
          }
        }

        return false;
      })
      .map((node) => {
        if (node.children && node.children.length > 0) {
          // If this node matches, keep all children
          if (matchesNode(node)) {
            return {
              ...node,
              children: JSON.parse(JSON.stringify(node.children)),
            };
          }

          // Otherwise, only keep matching children
          const filteredChildren = filterData(node.children, term);
          return {
            ...node,
            children: filteredChildren,
          };
        }
        return node;
      });
  }, []);

  // Handle sort toggle (asc -> desc -> none)
  const onSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        if (sortOrder === 1) {
          setSortOrder(-1);
        } else if (sortOrder === -1) {
          setSortField('');
          setSortOrder(0);
        } else {
          setSortField(field);
          setSortOrder(1);
        }
      } else {
        setSortField(field);
        setSortOrder(1);
      }
    },
    [sortField, sortOrder, setSortField, setSortOrder],
  );

  // Apply filter and sort
  const filteredData = useMemo(() => {
    const filtered = filterData(treeTableData, searchTerm);
    return applyCustomSort(filtered);
  }, [filterData, treeTableData, searchTerm, applyCustomSort]);

  // Build parent org name lookup map
  const parentOrgNameById = useMemo(() => {
    const byId = new Map<string, FlatOrgData>(data.map((item) => [item.id, item]));
    const result = new Map<string, string | null>();

    const normalizeParentName = (parentName?: string | null) => {
      const normalized = parentName?.trim();
      if (!normalized || normalized.toLowerCase() === 'cydea') {
        return null;
      }
      return normalized;
    };

    const findParentOrg = (startId?: string | null) => {
      let currentId = startId ?? null;
      const visited = new Set<string>();

      while (currentId) {
        if (visited.has(currentId)) break;
        visited.add(currentId);

        const parent = byId.get(currentId);
        if (!parent) break;

        if (parent.recordType === 'O') {
          return parent;
        }

        currentId = parent.parentId ?? null;
      }

      return null;
    };

    data.forEach((item) => {
      if (item.recordType === 'W') {
        // For workspaces: get parent org of the workspace's org
        const orgParent = findParentOrg(item.parentId);
        const orgParentParent = orgParent ? findParentOrg(orgParent.parentId) : null;
        result.set(item.id, normalizeParentName(orgParentParent?.name));
        return;
      }

      // For orgs: get direct parent org
      const directOrgParent = findParentOrg(item.parentId);
      result.set(item.id, normalizeParentName(directOrgParent?.name));
    });

    return result;
  }, [data]);

  // Build level lookup map from filtered tree data
  const levelById = useMemo(() => {
    const result = new Map<string, number>();

    const traverse = (nodes: FlatOrgTreeTableNode[]) => {
      for (const node of nodes) {
        result.set(node.data.id, node.level);
        if (node.children?.length) {
          traverse(node.children);
        }
      }
    };

    traverse(filteredData);
    return result;
  }, [filteredData]);

  return {
    filteredData,
    expandedKeys,
    setExpandedKeys,
    sortField,
    sortOrder,
    onSort,
    parentOrgNameById,
    levelById,
  };
};
